import { Box, LinearProgress, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import { useDeleteAlternative } from "../../hooks/useDeleteAlternative"
import { useGetAlternativesList } from "../../hooks/useGetAlternativesList"

import { AddAlternativeDialog } from "./AddAlternativeDialog"
import { ProductAlternativesTable } from "./ProductAlternativesTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import ToggleInactiveButton from "@/components/common/ToggleInactiveButton"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { ACTIVE_PRODUCT_STATUSES } from "@/screens/Products/constants"

interface ProductAlternativesPanelProps {
  companyProductAssociationLocationId: string
  shipToLocation?: { name?: string | null; city?: string | null; regionIsoCode?: string | null } | null
  productPublicId?: string
}

export const ProductAlternativesPanel: FC<ProductAlternativesPanelProps> = ({
  companyProductAssociationLocationId,
  shipToLocation,
  productPublicId,
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [deletingAlternativeId, setDeletingAlternativeId] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const { hasPermissions } = usePermissionManager()
  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const { alternatives, isFetching, refetch } = useGetAlternativesList({ companyProductAssociationLocationId })
  const { deleteAlternative } = useDeleteAlternative()

  const filteredAlternatives = showInactive
    ? alternatives
    : alternatives.filter((alt) => {
        const status = alt.alternateCompanyProductAssociationLocation?.product?.computedProductStatus
        return !status || ACTIVE_PRODUCT_STATUSES.includes(status)
      })

  const locationName = shipToLocation?.name
  const locationAddress = [shipToLocation?.city, shipToLocation?.regionIsoCode].filter(Boolean).join(", ")

  const handleDeleteConfirm = async () => {
    if (!deletingAlternativeId) return
    const success = await deleteAlternative(deletingAlternativeId)
    if (success) {
      refetch()
    }
    setDeletingAlternativeId(null)
  }

  return (
    <Box className='p-6'>
      {isFetching && <LinearProgress />}

      {!isFetching && filteredAlternatives.length > 0 && (
        <>
          <Box className='mb-6 flex items-center justify-between'>
            <Typography className='text-ml text-gray-900'>
              {`Here's the list of alternates for your selected product shipped to "`}
              <span className='font-semibold'>{locationName}</span>
              {`${locationAddress ? ` - ${locationAddress}` : ""}"`}
            </Typography>
            <Box className='flex items-center gap-2'>
              <ToggleInactiveButton
                showInactive={showInactive}
                onToggle={() => setShowInactive((prev) => !prev)}
                label='Products'
                size='small'
              />
              {canEdit && (
                <Button
                  appearance='outlined'
                  size='small'
                  startIcon={<AddOutlined />}
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  Add Alternate
                </Button>
              )}
            </Box>
          </Box>

          <ProductAlternativesTable
            alternatives={filteredAlternatives}
            isFetching={isFetching}
            onDelete={(id) => setDeletingAlternativeId(id)}
            canEdit={canEdit}
          />
        </>
      )}

      {!isFetching && filteredAlternatives.length === 0 && (
        <InformationMissingAlert
          title='No alternates have been added yet.'
          action={
            canEdit && (
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='text-primary'
                startIcon={<AddOutlined />}
                onClick={() => setIsAddDialogOpen(true)}
              >
                Add Alternate
              </Button>
            )
          }
        />
      )}

      {isAddDialogOpen && (
        <AddAlternativeDialog
          companyProductAssociationLocationId={companyProductAssociationLocationId}
          productId={productPublicId}
          onClose={() => setIsAddDialogOpen(false)}
          onSuccess={refetch}
        />
      )}

      {deletingAlternativeId && (
        <ConfirmationDialog
          title='Remove Alternative'
          content='Are you sure you want to remove this alternative product?'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          isWarning
          onClose={() => setDeletingAlternativeId(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </Box>
  )
}
